/**
 * Packaging OS — Public request form (MM7).
 *
 * One honest form for access / demo / packaging-audit / pilot requests.
 * Stores a lead via POST /api/public/requests; explicitly tells the
 * user that a human reviews it and no automated email is sent.
 */

import { useState } from "react";
import { useSearchParams } from "react-router-dom";
import { postPublicRequest } from "../api/client";
import { Button } from "../components/Button";
import { Footer } from "../components/Footer";
import { TextField } from "../components/FormField";
import { PublicHeader } from "../components/PublicHeader";
import { TrustBanner } from "../components/TrustBanner";
import { strings } from "../i18n/strings";
import { LOCALES } from "../i18n/locales";
import { useLocale } from "../i18n/useLocale";
import { useLocaleSwitch } from "../i18n/useLocaleSwitch";

const REQUEST_TYPES = ["access", "demo", "packaging_audit", "pilot"] as const;

type SubmitState =
  | { kind: "idle" }
  | { kind: "submitting" }
  | { kind: "done"; requestId: string }
  | { kind: "error"; message: string };

export function RequestAccessPage(): JSX.Element {
  const [locale] = useLocale();
  const setLocale = useLocaleSwitch();
  const t = strings(locale);
  const [params] = useSearchParams();

  const [requestType, setRequestType] = useState<string>("access");
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [company, setCompany] = useState("");
  const [country, setCountry] = useState("");
  const [preferredLanguage, setPreferredLanguage] = useState<string>(locale);
  const [message, setMessage] = useState("");
  const [state, setState] = useState<SubmitState>({ kind: "idle" });

  const typeLabels: Record<(typeof REQUEST_TYPES)[number], string> = {
    access: t.requestTypeAccess,
    demo: t.requestTypeDemo,
    packaging_audit: t.requestTypeAudit,
    pilot: t.requestTypePilot,
  };

  async function submit(): Promise<void> {
    if (name.trim().length < 2 || !/^[^@\s]+@[^@\s]+\.[^@\s]{2,}$/.test(email)) {
      setState({ kind: "error", message: t.requestValidationError });
      return;
    }
    setState({ kind: "submitting" });
    const result = await postPublicRequest({
      request_type: requestType,
      requester_name: name.trim(),
      email: email.trim(),
      company: company.trim() || undefined,
      country: country.trim() || undefined,
      preferred_language: preferredLanguage,
      service_pack_id: params.get("pack") ?? undefined,
      message: message.trim() || undefined,
      source_page: "/request",
    });
    if (result.outcome === "ok" && result.data?.stored) {
      setState({ kind: "done", requestId: result.data.request_id ?? "" });
    } else {
      setState({
        kind: "error",
        message: result.errorMessage ?? t.requestValidationError,
      });
    }
  }

  return (
    <div className="po-page">
      <PublicHeader
        locale={locale}
        onLocaleChange={setLocale}
        switchLanguageLabel={t.switchLanguageLabel}
        brand={t.appName}
        navLinks={[{ to: "/services", label: t.servicesTitle }]}
      />

      <main className="po-page-main">
        <TrustBanner message={t.trustBannerShort} />

        <section aria-labelledby="po-request-title" className="po-hero">
          <h1 id="po-request-title" className="po-hero__title">
            {t.requestTitle}
          </h1>
          <p className="po-hero__subtitle">{t.requestIntro}</p>
        </section>

        {state.kind === "done" ? (
          <section role="status" data-testid="request-success">
            <h2>{t.requestSuccess}</h2>
            <p>{t.requestNoEmailNote}</p>
          </section>
        ) : (
          <form
            className="po-form"
            data-testid="request-form"
            onSubmit={(e) => {
              e.preventDefault();
              void submit();
            }}
          >
            <fieldset>
              <legend>{t.requestTypeLabel}</legend>
              {REQUEST_TYPES.map((type) => (
                <label key={type} className="po-radio">
                  <input
                    type="radio"
                    name="request_type"
                    value={type}
                    checked={requestType === type}
                    onChange={() => setRequestType(type)}
                  />
                  {typeLabels[type]}
                </label>
              ))}
            </fieldset>

            <TextField
              label={t.requestNameLabel}
              required
              value={name}
              onChange={setName}
              maxLength={200}
            />
            <TextField
              label={t.requestEmailLabel}
              required
              type="email"
              value={email}
              onChange={setEmail}
              maxLength={200}
            />
            <TextField
              label={t.requestCompanyLabel}
              value={company}
              onChange={setCompany}
              maxLength={200}
            />
            <TextField
              label={t.requestCountryLabel}
              value={country}
              onChange={setCountry}
              maxLength={100}
            />

            <label className="po-field">
              <span className="po-field__label">{t.requestLanguageLabel}</span>
              <select
                value={preferredLanguage}
                onChange={(e) => setPreferredLanguage(e.target.value)}
                data-testid="preferred-language"
              >
                {LOCALES.map((l) => (
                  <option key={l.code} value={l.code}>
                    {l.nativeLabel}
                  </option>
                ))}
              </select>
            </label>

            <label className="po-field">
              <span className="po-field__label">{t.requestMessageLabel}</span>
              <textarea
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                maxLength={4000}
                rows={4}
              />
            </label>

            {state.kind === "error" ? (
              <p role="alert" className="po-error">
                {state.message}
              </p>
            ) : null}

            <Button variant="primary" type="submit"
                    disabled={state.kind === "submitting"}>
              {state.kind === "submitting" ? t.requestSubmitting : t.requestSubmit}
            </Button>
            <p className="po-disclaimer">{t.requestNoEmailNote}</p>
          </form>
        )}
      </main>

      <Footer copyright={t.copyrightLine} />
    </div>
  );
}
